import { forwardRef, useEffect, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';

import clsx from 'clsx';

import { SingleSelect } from '@/common';
import { ColorPicker, Toolbar } from '@/components/generic';
import { NumberStepper } from '@/components/generic/NumberStepper/NumberStepper';
import { CANVAS_OBJECT_TYPE, fonts } from '@/constants';
import { ALIGNMENT, EDITABLE_TEXT_PROPERTIES, FONT_STYLE, FONT_WEIGHT, SIZE } from '@/enums';
import { areAllSelectedOfType, getCanvasFromRef } from '@/helpers';
import {
  DEFAULT_FONT_SIZES,
  getCommonStateValues,
  getUniformStyleProps
} from '@/helpers/TextOverlay';
import {
  AlignCenterIcon,
  AlignLeftIcon,
  AlignRightIcon,
  BoldIcon,
  ItalicIcon,
  UnderlineIcon
} from '@/resources';
import { TextStyleType } from '@/types';

import {
  initialTextStyle,
  TEXT_STYLE_ACTIONS,
  TextStyleActionType,
  textStyleReducer
} from './TextStyle.reducer';

interface TextOverlayControlsProps {
  selectedObjects: Record<string, fabric.Object>;
  setSelectedObjects: (objects: Record<string, fabric.Object>) => void;
  setHasCanvasChanged: (value: boolean) => void;
}

const TextOverlayControls = forwardRef<fabric.Canvas | null, TextOverlayControlsProps>(
  ({ selectedObjects, setSelectedObjects, setHasCanvasChanged }, canvasRef) => {
    const { t } = useTranslation('common');

    const [isMixedTypeSelected, setIsMixedTypeSelected] = useState(false);
    const [isColorPickerOpen, setIsColorPickerOpen] = useState(false);

    const [textStyle, dispatch] = useReducer(textStyleReducer, initialTextStyle);

    useEffect(() => {
      const selectedIds = Object.keys(selectedObjects);
      if (selectedIds.length > 0) {
        setIsMixedTypeSelected(
          areAllSelectedOfType(selectedIds, selectedObjects, CANVAS_OBJECT_TYPE.TEXTBOX)
        );

        const uniformValues = getUniformStyleProps(selectedObjects, selectedIds);
        const comparisonBaseValues = selectedObjects[selectedIds[0]] as fabric.Textbox;
        const newState = getCommonStateValues(comparisonBaseValues, textStyle, uniformValues);

        dispatch({ type: TEXT_STYLE_ACTIONS.SET_ALL_STYLES, value: newState as TextStyleType });
      } else {
        dispatch({ type: TEXT_STYLE_ACTIONS.SET_ALL_STYLES, value: initialTextStyle });
        setIsMixedTypeSelected(false);
      }
    }, [selectedObjects]);

    const TEXT_FORMATTING_CONFIG = [
      {
        handleItemClick: () =>
          changeTextStyle(EDITABLE_TEXT_PROPERTIES.FONT_WEIGHT, {
            value:
              textStyle.fontWeight === FONT_WEIGHT.BOLD ? FONT_WEIGHT.NORMAL : FONT_WEIGHT.BOLD,
            type: TEXT_STYLE_ACTIONS.SET_FONT_WEIGHT
          }),
        icon: () => BoldIcon({ className: 'size-4' }),
        isSelected: textStyle.fontWeight === FONT_WEIGHT.BOLD
      },
      {
        handleItemClick: () =>
          changeTextStyle(EDITABLE_TEXT_PROPERTIES.UNDERLINE, {
            value: !textStyle.underline,
            type: TEXT_STYLE_ACTIONS.SET_UNDERLINE
          }),
        icon: () => UnderlineIcon({ className: 'size-4' }),
        isSelected: textStyle.underline
      },
      {
        handleItemClick: () =>
          changeTextStyle(EDITABLE_TEXT_PROPERTIES.FONT_STYLE, {
            type: TEXT_STYLE_ACTIONS.SET_FONT_STYLE,
            value: textStyle.fontStyle === FONT_STYLE.ITALIC ? FONT_STYLE.NORMAL : FONT_STYLE.ITALIC
          }),
        isSelected: textStyle.fontStyle === FONT_STYLE.ITALIC,
        icon: () => ItalicIcon({ className: 'size-4' })
      }
    ];

    const TEXT_ALIGN_CONFIG = [
      {
        handleItemClick: () =>
          changeTextStyle(EDITABLE_TEXT_PROPERTIES.TEXT_ALIGN, {
            type: TEXT_STYLE_ACTIONS.SET_ALIGNMENT,
            value: ALIGNMENT.LEFT
          }),
        icon: () => AlignLeftIcon({ className: 'size-4' }),
        isSelected: textStyle.textAlign === ALIGNMENT.LEFT
      },
      {
        handleItemClick: () =>
          changeTextStyle(EDITABLE_TEXT_PROPERTIES.TEXT_ALIGN, {
            type: TEXT_STYLE_ACTIONS.SET_ALIGNMENT,
            value: ALIGNMENT.CENTER
          }),
        icon: () => AlignCenterIcon({ className: 'size-4' }),
        isSelected: textStyle.textAlign === ALIGNMENT.CENTER
      },
      {
        handleItemClick: () =>
          changeTextStyle(EDITABLE_TEXT_PROPERTIES.TEXT_ALIGN, {
            type: TEXT_STYLE_ACTIONS.SET_ALIGNMENT,
            value: ALIGNMENT.RIGHT
          }),
        icon: () => AlignRightIcon({ className: 'size-4' }),
        isSelected: textStyle.textAlign === ALIGNMENT.RIGHT
      }
    ];

    const changeTextStyle = (property: EDITABLE_TEXT_PROPERTIES, data: TextStyleActionType) => {
      const canvas = getCanvasFromRef(canvasRef);
      if (!canvas || isDisabled) {
        return;
      }
      canvas.getObjects().forEach((obj) => {
        if (selectedObjects[obj.data?.id]) {
          (obj as fabric.Textbox).set(property as keyof fabric.Textbox, data.value);
        }
      });
      canvas.requestRenderAll();
      setHasCanvasChanged(true);

      dispatch(data);
    };

    const handleTextareaBlur = () => {
      let hasTextChanged = false;
      const newSelectedObjects = { ...selectedObjects };
      Object.keys(newSelectedObjects).forEach((id) => {
        const obj = newSelectedObjects[id] as fabric.Textbox;
        if (obj.text !== textStyle.text) {
          obj.text = textStyle.text;
          hasTextChanged = true;
        }
      });
      setSelectedObjects(newSelectedObjects);
      if (hasTextChanged) {
        setHasCanvasChanged(true);
      }
    };

    const isDisabled = Object.keys(selectedObjects).length === 0 || isMixedTypeSelected;

    return (
      <div className='mb-5 mt-8 flex w-full flex-col gap-3 rounded-xl border border-blue-powder p-3 md:h-40 2xl:h-48'>
        <div className='flex w-full flex-col justify-between gap-2 md:h-9 md:flex-row'>
          <div className='flex gap-2'>
            <SingleSelect
              id='select-font-family'
              handleChange={(value) =>
                changeTextStyle(EDITABLE_TEXT_PROPERTIES.FONT_FAMILY, {
                  type: TEXT_STYLE_ACTIONS.SET_FONT_FAMILY,
                  value: value?.value as string
                })
              }
              disabled={isDisabled}
              options={fonts}
              borderStyle='1px solid var(--color-blue-powder)'
              className={clsx('w-48', isDisabled && 'pointer-events-none opacity-50')}
              placeholder={
                isMixedTypeSelected || !textStyle.fontFamily ? t('mixedStyle') : t('fontFamily')
              }
              controlStyles={{ height: '36px', minHeight: '36px' }}
              placeholderStyles={{ marginTop: '-20px' }}
              value={
                isMixedTypeSelected
                  ? null
                  : fonts.find((f) => f.value === textStyle.fontFamily) || null
              }
            />
            <NumberStepper
              values={DEFAULT_FONT_SIZES}
              currentValue={textStyle.fontSize}
              onChange={(val) => {
                changeTextStyle(EDITABLE_TEXT_PROPERTIES.FONT_SIZE, {
                  type: TEXT_STYLE_ACTIONS.SET_FONT_SIZE,
                  value: val
                });
              }}
              className='h-9 text-slate-gray md:h-auto'
              isDisabled={isDisabled}
              placeholder={isMixedTypeSelected || !textStyle.fontSize ? t('mix') : undefined}
            />
          </div>
          <div className='flex gap-2 text-slate-gray'>
            <Toolbar isDisabled={isDisabled} items={TEXT_FORMATTING_CONFIG} size={SIZE.MEDIUM} />
            <ColorPicker
              isOpen={isColorPickerOpen}
              isDisabled={isDisabled}
              setIsOpen={setIsColorPickerOpen}
              onChange={(color) =>
                changeTextStyle(EDITABLE_TEXT_PROPERTIES.FILL, {
                  value: color,
                  type: TEXT_STYLE_ACTIONS.SET_COLOR
                })
              }
              color={textStyle.fill}
            />
            <Toolbar isDisabled={isDisabled} items={TEXT_ALIGN_CONFIG} size={SIZE.MEDIUM} />
          </div>
        </div>

        <div className='flex w-full flex-1 flex-col items-center justify-center rounded-xl border border-blue-powder bg-ghost-white text-slate-gray'>
          {isMixedTypeSelected || Object.keys(selectedObjects).length > 1 ? (
            <div className='p-2 text-center text-sm'>{t('mixedContentSelected')}</div>
          ) : (
            <textarea
              id='test'
              disabled={Object.keys(selectedObjects).length !== 1}
              className='size-full resize-none rounded-xl p-3 focus-visible:outline-blue-powder disabled:pointer-events-none disabled:opacity-50'
              value={textStyle.text}
              onBlur={handleTextareaBlur}
              onChange={(e) =>
                changeTextStyle(EDITABLE_TEXT_PROPERTIES.TEXT, {
                  type: TEXT_STYLE_ACTIONS.SET_TEXT_VALUE,
                  value: e.target.value
                })
              }
            />
          )}
        </div>
      </div>
    );
  }
);

TextOverlayControls.displayName = 'TextOverlayControls';
export { TextOverlayControls };
